<script lang="ts">
import { defineComponent, nextTick } from 'vue'
import { LIURole, useLoggedInUserStore } from '@/stores/loggedInUser'
import { StudentIdentificationMethod } from '@/types/kioskTypes'
import { useDisplayConfigStore } from '@/stores/displayConfig'
import SimpleKeyboard from './SimpleKeyboard.vue'

const BUFF_ONECARD_LENGTH = 16
const STUDENT_ID_LENGTH = 9

const LAYOUTS = {
  numeric: ['1 2 3', '4 5 6', '7 8 9', '{bksp} 0 {enter}']
}

export interface FieldConfig {
  key: string
  label: string
  obfuscated?: boolean
  autoSubmitLength?: number
  keyboardLayout?: 'numeric' | 'default'
  cols?: number
}

export default defineComponent({
  name: 'StudentIdentifier',
  components: { SimpleKeyboard },
  props: {
    buttonText: {
      type: String,
      default: 'Submit'
    },
    hideButton: {
      type: Boolean,
      default: false
    },
    clearWhenFullyEntered: {
      type: Boolean,
      default: false
    },
    signInMethods: {
      type: Array as () => StudentIdentificationMethod[],
      default: () => [StudentIdentificationMethod.BOC]
    }
  },
  data() {
    return {
      KioskState: StudentIdentificationMethod,
      LIURole,
      userStore: useLoggedInUserStore(),
      displayStore: useDisplayConfigStore(),
      formValues: {} as Record<string, string>,
      activeFieldKey: '',
      internalKioskState: this.signInMethods?.[0] || StudentIdentificationMethod.BOC
    }
  },
  computed: {
    stateConfig(): FieldConfig[] {
      const { displayStore } = this
      const actionText = displayStore.isKiosk ? 'Swipe' : 'Enter'

      switch (this.kioskState) {
        case StudentIdentificationMethod.BOC:
          return [
            {
              key: StudentIdentificationMethod.BOC,
              label: `${actionText} your Buff OneCard`,
              obfuscated: true,
              autoSubmitLength: BUFF_ONECARD_LENGTH
            }
          ]
        case StudentIdentificationMethod.SID:
          return [
            {
              key: StudentIdentificationMethod.SID,
              label: 'Enter Student ID',
              autoSubmitLength: STUDENT_ID_LENGTH,
              keyboardLayout: 'numeric'
            }
          ]
        case StudentIdentificationMethod.Identikey:
          return [{ key: StudentIdentificationMethod.Identikey, label: 'Enter Identikey' }]
        case StudentIdentificationMethod.IdentikeyAndSID:
          return [
            { key: StudentIdentificationMethod.Identikey, label: 'Identikey', cols: 6 },
            {
              key: StudentIdentificationMethod.SID,
              label: 'Student ID',
              keyboardLayout: 'numeric',
              cols: 6
            }
          ]
        default:
          return []
      }
    },
    activeFieldConfig(): FieldConfig | undefined {
      return this.stateConfig.find((f) => f.key === this.activeFieldKey)
    },
    keypadLayout() {
      if (this.activeFieldConfig?.keyboardLayout === 'numeric') return { default: LAYOUTS.numeric }
      return undefined
    },
    kioskState: {
      get() {
        return this.internalKioskState
      },
      set(newKioskState: StudentIdentificationMethod) {
        this.formValues = {}
        this.activeFieldKey = ''
        this.internalKioskState = newKioskState
      }
    }
  },
  watch: {
    stateConfig: {
      immediate: true,
      handler(newConfig) {
        if (newConfig.length > 0 && !this.activeFieldKey) {
          this.activeFieldKey = newConfig[0].key
        }
      }
    }
  },
  emits: [
    StudentIdentificationMethod.BOC,
    StudentIdentificationMethod.SID,
    StudentIdentificationMethod.Identikey,
    StudentIdentificationMethod.IdentikeyAndSID
  ],
  methods: {
    async focusInput(key?: string) {
      await nextTick()
      const targetKey = key || this.stateConfig[0]?.key
      if (!targetKey) return

      const inputRef = (this.$refs[`input_${targetKey}`] as any)?.[0] as HTMLInputElement
      if (inputRef) {
        inputRef.focus()
        this.activeFieldKey = targetKey
      }
    },
    updateValue(key: string, value: string) {
      this.formValues[key] = value
      this.checkAutoSubmit(key, value)
    },
    checkAutoSubmit(key: string, value: string) {
      if (!this.clearWhenFullyEntered) return

      const fieldConfig = this.stateConfig.find((f) => f.key === key)
      if (fieldConfig?.autoSubmitLength && value.length === fieldConfig.autoSubmitLength) {
        const allFieldsFilled = this.stateConfig.every((f) => !!this.formValues[f.key])

        if (allFieldsFilled) {
          this.doSubmit()
        }
      }
    },
    doSubmit() {
      const payload = { ...this.formValues }

      const isValid = this.stateConfig.every(
        (f) => payload[f.key] && payload[f.key].trim().length > 0
      )
      if (!isValid) return

      this.$emit(this.kioskState, payload)

      if (this.clearWhenFullyEntered) {
        this.formValues = {}
      }
    },
    handleKeyPress(button: string) {
      if (!this.activeFieldKey) return
      let currentValue = this.formValues[this.activeFieldKey] || ''

      switch (button) {
        case '{enter}':
          this.doSubmit()
          break
        case '{bksp}':
          this.updateValue(this.activeFieldKey, currentValue.slice(0, -1))
          break
        case '{space}':
          this.updateValue(this.activeFieldKey, currentValue + ' ')
          break
        default:
          if (RegExp('^{.*}$').test(button)) break
          this.updateValue(this.activeFieldKey, currentValue + button)
          break
      }
    }
  }
})
</script>

<template>
  <v-sheet class="pa-7 pt-7" :width="'100%'" :min-width="350" :max-width="750" :elevation="4">
    <v-form @submit.prevent="doSubmit" validate-on="lazy">
      <v-container>
        <v-row>
          <v-col cols="12">
            <slot name="description"> </slot>
          </v-col>
        </v-row>

        <v-row>
          <v-btn-toggle
            divided
            color="primary"
            class="mx-auto"
            variant="plain"
            v-model="kioskState"
            @click="focusInput()"
            mandatory
          >
            <!-- in a perfect world these would be driven by the config too - but I'm feeling lazy :^) -->
            <v-btn v-if="signInMethods.includes(KioskState.BOC)" :value="KioskState.BOC">
              <span class="hidden-sm-and-down"
                >{{ displayStore.isKiosk ? 'Swipe' : 'Enter' }}&nbsp;</span
              >
              <span>BuffOne</span>
            </v-btn>

            <v-btn v-if="signInMethods.includes(KioskState.SID)" :value="KioskState.SID">
              <span class="hidden-sm-and-down">Enter&nbsp;</span>
              <span>Student ID</span>
            </v-btn>

            <v-btn
              v-if="signInMethods.includes(KioskState.Identikey)"
              :value="KioskState.Identikey"
            >
              <span class="hidden-sm-and-down">Enter&nbsp;</span>
              <span>Identikey</span>
            </v-btn>

            <v-btn
              v-if="signInMethods.includes(KioskState.IdentikeyAndSID)"
              :value="KioskState.IdentikeyAndSID"
            >
              <span class="hidden-sm-and-down">Enter&nbsp;</span>
              <span>Identikey &amp; SID</span>
            </v-btn>
          </v-btn-toggle>
        </v-row>

        <v-row>
          <v-col
            v-for="field in stateConfig"
            :key="field.key"
            :cols="field.cols || 12"
            class="px-1"
          >
            <v-text-field
              :ref="`input_${field.key}`"
              :model-value="formValues[field.key]"
              @update:model-value="(val) => updateValue(field.key, val)"
              @focus="activeFieldKey = field.key"
              :label="field.label"
              density="compact"
              variant="outlined"
              hide-details="auto"
              autocomplete="off"
              :required="true"
              :autofocus="field.key === stateConfig[0].key"
              :class="{ 'obfuscated-text': field.obfuscated }"
            >
            </v-text-field>
          </v-col>
        </v-row>

        <v-expand-transition>
          <v-row v-if="displayStore.isKiosk && kioskState !== KioskState.BOC">
            <SimpleKeyboard :layout="keypadLayout" @onKeyPress="handleKeyPress" />
          </v-row>
        </v-expand-transition>

        <v-row>
          <v-col cols="12" v-if="!hideButton" class="pb-0 mt-3">
            <div class="d-flex justify-center">
              <v-btn type="submit" color="primary" size="x-large" variant="tonal">{{
                buttonText
              }}</v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </v-sheet>
</template>

<style scoped>
.obfuscated-text :deep(input) {
  -webkit-text-security: disc !important;
}
</style>
